import { useState, useEffect, useCallback, useRef } from 'react';
import type { TransactionApiRecord, TransactionListMeta, FeeAllocation } from '../../services/transactionApi';
import { fetchTransactions, fetchTransactionById } from '../../services/transactionApi';
import { fetchInvoiceById } from '../../services/invoiceApi';
import type { InvoiceDetailItem } from '../../services/invoiceApi';
import { fetchClasses, fetchSections } from '../../services/studentApi';
import type { ClassOption, SectionOption } from '../../services/studentApi';
import { printReceipt, openReceiptWindow, toReceiptStudent } from '../../utils/printReceipt';
import type { ReceiptData } from '../../utils/printReceipt';
import { breakdownFromApiItems } from '../../utils/feeBreakdown';
import { formatMonth, formatMonthRange, monthsReceiptParts } from '../collection/months';
import { useCurrentSchool } from '../../hooks/useCurrentSchoolName';
import RupeeIcon from '../icons/RupeeIcon';
import './TransactionLedger.css';
import { useTranslation } from 'react-i18next';

function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const METHOD_LABELS: Record<string, string> = {
    cash: 'Cash',
    cheque: 'Cheque',
    online: 'Online',
    bank_transfer: 'Bank Transfer',
    pos: 'POS',
};

const PER_PAGE = 16;

/**
 * What a transaction turns out to be, once the server has been asked for the detail a
 * receipt needs. A payment collected across months settles several invoices, so the one
 * invoice the ledger row names is only ever part of the story.
 *
 *  - `months`  — `allocations` is the whole record: every month, what it was billed, what
 *                it still owes. Printed exactly as the collection desk prints it.
 *  - `invoice` — the classic single-invoice payment; its lines come from the invoice.
 *  - `bare`    — neither is available, so only the payment itself can be printed.
 */
type TxDetail =
    | { kind: 'months'; allocations: FeeAllocation[]; unallocated: number }
    | { kind: 'invoice'; items: InvoiceDetailItem[]; issueDate: string; balanceDue: number }
    | { kind: 'bare' };

/* Lazy-loaded, cached per transaction for the session. */
type TxDetailState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'loaded'; detail: TxDetail };

/** The allocations a record carries, if it is a months payment at all. */
function allocationsOf(tx: TransactionApiRecord): TxDetail | null {
    if (!tx.allocations || tx.allocations.length === 0) return null;
    return {
        kind: 'months',
        allocations: tx.allocations,
        unallocated: parseFloat(tx.unallocated_amount ?? '0') || 0,
    };
}

function getStudentDisplayName(tx: TransactionApiRecord): string {
    return tx.student
        ? (tx.student.name || [tx.student.first_name, tx.student.last_name].filter(Boolean).join(' ') || '—')
        : '—';
}

function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function currentMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/* A months payment settles several invoices, so naming just the one the record points at
   would understate it. Only possible when the list carries the allocations; otherwise the
   row reads as it always has, and the months surface on the receipt and in the drawer. */
function invoiceLabel(tx: TransactionApiRecord): string {
    const allocations = tx.allocations ?? [];
    if (allocations.length > 1) return `${allocations[0].invoice_number} +${allocations.length - 1}`;
    return tx.invoice?.invoice_number ?? allocations[0]?.invoice_number ?? '—';
}

function invoiceTitle(tx: TransactionApiRecord): string | undefined {
    const allocations = tx.allocations ?? [];
    if (allocations.length <= 1) return undefined;
    return `${formatMonthRange(allocations.map((a) => a.month))} · ${allocations.map((a) => a.invoice_number).join(', ')}`;
}

function formatClassSection(
    cls: { name: string } | null | undefined,
    sec: { name: string } | null | undefined,
): string {
    const className = cls?.name?.replace(/^Class\s+/i, '').trim() ?? '';
    const sectionName = sec?.name?.trim() ?? '';
    const combined = [className, sectionName].filter(Boolean).join(' ');
    return combined || '—';
}

export default function TransactionLedger() {
    const { t: _t } = useTranslation();
    const school = useCurrentSchool();

    const [transactions, setTransactions] = useState<TransactionApiRecord[]>([]);
    const [meta, setMeta] = useState<TransactionListMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    /* ── Details drawer ── */
    const [drawerTx, setDrawerTx] = useState<TransactionApiRecord | null>(null);
    const [detailByTx, setDetailByTx] = useState<Record<string, TxDetailState>>({});
    /* A server that doesn't expand a single transaction 404s. Worth asking once — not once
       per receipt — since it is the only place allocations may be hiding. */
    const noTxEndpoint = useRef(false);

    /* ── Draft filter inputs (not yet applied) ── */
    const [studentName, setStudentName] = useState('');
    const [classInput, setClassInput] = useState('');
    const [sectionInput, setSectionInput] = useState('');
    const [invoiceId, setInvoiceId] = useState('');
    const [_page, setPage] = useState(1);

    /* ── Month scope (commits immediately, like perPage) ── */
    const [month, setMonth] = useState(currentMonth());

    /* ── Committed filter values (applied on Search) ── */
    const [appliedStudentName, setAppliedStudentName] = useState('');
    const [appliedClassId, setAppliedClassId] = useState('');
    const [appliedSectionId, setAppliedSectionId] = useState('');
    const [appliedInvoiceId, setAppliedInvoiceId] = useState('');

    /* ── Dropdown options ── */
    const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
    const [availableSections, setAvailableSections] = useState<SectionOption[]>([]);

    /* Load classes on mount */
    useEffect(() => {
        fetchClasses()
            .then((classes) => setAvailableClasses(classes))
            .catch(() => { /* silently fall back to empty */ });
    }, []);

    /* Cascade: reload sections when class selection changes */
    useEffect(() => {
        if (classInput) {
            fetchSections(classInput)
                .then((sections) => setAvailableSections(sections))
                .catch(() => setAvailableSections([]));
        } else {
            setAvailableSections([]);
        }
        setSectionInput('');
    }, [classInput]);

    const loadData = useCallback(async (
        p: number,
        name: string,
        classId: string,
        sectionId: string,
        iId: string,
        monthVal: string,
    ) => {
        setLoading(true);
        setError('');
        try {
            const res = await fetchTransactions({
                student_name: name || undefined,
                class_id: classId || undefined,
                section_id: sectionId || undefined,
                invoice_id: iId || undefined,
                month: monthVal || undefined,
                per_page: PER_PAGE,
                page: p,
            });
            setTransactions(res.data);
            setMeta(res.meta);
            setDrawerTx(null);
        } catch {
            setError('Failed to load transactions.');
            setTransactions([]);
            setMeta(null);
        } finally {
            setLoading(false);
        }
    }, []);

    /* Initial load */
    useEffect(() => {
        loadData(1, '', '', '', '', month);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = () => {
        const name = studentName.trim();
        const iId = invoiceId.trim();
        setAppliedStudentName(name);
        setAppliedClassId(classInput);
        setAppliedSectionId(sectionInput);
        setAppliedInvoiceId(iId);
        setPage(1);
        loadData(1, name, classInput, sectionInput, iId, month);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        loadData(newPage, appliedStudentName, appliedClassId, appliedSectionId, appliedInvoiceId, month);
    };

    const handleMonthChange = (newMonth: string) => {
        setMonth(newMonth);
        setPage(1);
        loadData(1, appliedStudentName, appliedClassId, appliedSectionId, appliedInvoiceId, newMonth);
    };

    const handleClear = () => {
        setStudentName('');
        setClassInput('');
        setSectionInput('');
        setInvoiceId('');
        setAppliedStudentName('');
        setAppliedClassId('');
        setAppliedSectionId('');
        setAppliedInvoiceId('');
        setPage(1);
        loadData(1, '', '', '', '', month);
    };

    const hasFilters = appliedStudentName || appliedClassId || appliedSectionId || appliedInvoiceId;

    /**
     * Everything a receipt needs for one transaction, in order of authority:
     *
     * 1. `allocations` on the record itself — a months payment, complete as it stands.
     * 2. the transaction endpoint — where a server that keeps the list lean puts them.
     * 3. the invoice the record names — the single-invoice payment, as before.
     *
     * Reprinting a months payment from its one named invoice would put a third of the money
     * on the paper, so the allocations are worth a request before falling back to it.
     */
    const resolveDetail = async (tx: TransactionApiRecord): Promise<TxDetail> => {
        const inline = allocationsOf(tx);
        if (inline) return inline;

        if (!noTxEndpoint.current) {
            try {
                const full = await fetchTransactionById(tx.id);
                const fetched = allocationsOf(full);
                if (fetched) return fetched;
            } catch {
                noTxEndpoint.current = true;
            }
        }

        if (!tx.invoice) return { kind: 'bare' };
        const inv = await fetchInvoiceById(tx.invoice.id);
        return {
            kind: 'invoice',
            items: inv.items ?? [],
            issueDate: inv.issue_date,
            balanceDue: parseFloat(inv.balance_amount),
        };
    };

    /** Resolve a transaction's detail once and cache it; the drawer and the receipt share it. */
    const loadDetail = (tx: TransactionApiRecord): Promise<TxDetail> => {
        const cached = detailByTx[tx.id];
        if (cached?.status === 'loaded') return Promise.resolve(cached.detail);
        setDetailByTx((prev) => ({ ...prev, [tx.id]: { status: 'loading' } }));
        return resolveDetail(tx)
            .then((detail) => {
                setDetailByTx((prev) => ({ ...prev, [tx.id]: { status: 'loaded', detail } }));
                return detail;
            })
            .catch((err) => {
                setDetailByTx((prev) => ({ ...prev, [tx.id]: { status: 'error' } }));
                throw err;
            });
    };

    const openDrawer = (tx: TransactionApiRecord) => {
        setDrawerTx(tx);
        const cached = detailByTx[tx.id];
        if (!cached || cached.status === 'error') loadDetail(tx).catch(() => { /* surfaced in the drawer */ });
    };

    /* The same receipt the collection desk prints. A months payment prints every month it
       covered, the fees it billed (a split fee regrouped into the one charge the payer
       handed money over for) and any advance; a single-invoice payment prints that
       invoice's lines at gross, its discounts and its billing month. With no detail at all
       only the payment itself can be printed — no line items, and the payment date standing
       in for the billing month. */
    const receiptData = (tx: TransactionApiRecord, detail?: TxDetail): ReceiptData => {
        const payment = {
            ...toReceiptStudent(tx.student, getStudentDisplayName(tx)),
            schoolName: school.name ?? undefined,
            schoolAddress: school.address ?? undefined,
            amountPaid: parseFloat(tx.amount_paid),
            paymentMethod: tx.payment_method,
            referenceNumber: tx.reference_number ?? '',
            collectedAt: tx.created_at,
        };

        if (detail?.kind === 'months') {
            return { ...payment, ...monthsReceiptParts(detail.allocations, detail.unallocated) };
        }

        const invoice = detail?.kind === 'invoice' ? detail : undefined;
        const b = invoice && invoice.items.length > 0 ? breakdownFromApiItems(invoice.items) : undefined;
        return {
            ...payment,
            invoiceNumber: tx.invoice?.invoice_number ?? '—',
            billingDate: invoice?.issueDate ?? tx.transaction_date,
            items: b?.charges,
            discounts: b?.discounts,
            subtotal: b?.subtotal,
            totalAmount: b?.total,
            // The invoice's outstanding balance. Only known once the detail is loaded —
            // a receipt printed without it simply omits the balance line.
            balanceDue: invoice?.balanceDue,
        };
    };

    const handlePrintReceipt = (tx: TransactionApiRecord) => {
        const cached = detailByTx[tx.id];
        if (cached?.status === 'loaded') { printReceipt(receiptData(tx, cached.detail)); return; }

        // Claim the window while we still have the click's user activation, then fill it
        // once the detail lands — otherwise the receipt would print bare.
        const win = openReceiptWindow();
        loadDetail(tx)
            .then((detail) => printReceipt(receiptData(tx, detail), win))
            .catch(() => printReceipt(receiptData(tx), win));
    };

    const from = meta ? (meta.current_page - 1) * meta.per_page + 1 : 0;
    const to = meta ? Math.min(meta.current_page * meta.per_page, meta.total) : 0;

    return (
        <div className="txns">
            <div className="txns__header">
                <div className="txns__header-text">
                    <h1 className="txns__title">Transaction Ledger</h1>
                    <p className="txns__subtitle">View and filter fee payment transactions.</p>
                </div>
            </div>

            {/* ── Filter Bar ── */}
            <div className="txns__toolbar">
                <div className="txns__toolbar-left">
                    <div className="txns__filter-field">
                        <label className="txns__filter-label" htmlFor="txnMonth">Month</label>
                        <input
                            id="txnMonth"
                            className="txns__filter-input"
                            type="month"
                            value={month}
                            onChange={(e) => handleMonthChange(e.target.value)}
                        />
                    </div>
                    <div className="txns__filter-field">
                        <label className="txns__filter-label" htmlFor="txnStudentName">Student Name</label>
                        <input
                            id="txnStudentName"
                            className="txns__filter-input"
                            type="text"
                            placeholder="Search by name…"
                            value={studentName}
                            onChange={(e) => setStudentName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        />
                    </div>
                    <div className="txns__filter-field">
                        <label className="txns__filter-label" htmlFor="txnClass">Class</label>
                        <select
                            id="txnClass"
                            className="txns__filter-select"
                            value={classInput}
                            onChange={(e) => setClassInput(e.target.value)}
                        >
                            <option value="">All classes</option>
                            {availableClasses.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="txns__filter-field">
                        <label className="txns__filter-label" htmlFor="txnSection">Section</label>
                        <select
                            id="txnSection"
                            className="txns__filter-select"
                            value={sectionInput}
                            onChange={(e) => setSectionInput(e.target.value)}
                            disabled={!classInput}
                        >
                            <option value="">All sections</option>
                            {availableSections.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="txns__filter-field">
                        <label className="txns__filter-label" htmlFor="txnInvoiceId">Invoice ID</label>
                        <input
                            id="txnInvoiceId"
                            className="txns__filter-input"
                            type="text"
                            placeholder="Filter by invoice…"
                            value={invoiceId}
                            onChange={(e) => setInvoiceId(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        />
                    </div>
                    <div className="txns__filter-actions">
                        <button className="txns__search-btn" onClick={handleSearch}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            Search
                        </button>
                        {hasFilters && (
                            <button className="txns__clear-btn" onClick={handleClear}>Clear</button>
                        )}
                    </div>
                </div>
                <div className="txns__toolbar-right">
                    {meta && <span className="txns__record-count">{meta.total} transaction{meta.total !== 1 ? 's' : ''}</span>}
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="txns__error-banner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {error}
                </div>
            )}

            {/* ── Table ── */}
            <div className="txns__table-wrap">
                {loading ? (
                    <div className="txns__loading">
                        <div className="txns__loader">
                            <span className="txns__loader-dot" />
                            <span className="txns__loader-dot" />
                            <span className="txns__loader-dot" />
                        </div>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="txns__empty">
                        <RupeeIcon size={36} strokeWidth={1.5} />
                        <h3 className="txns__empty-title">{hasFilters ? 'No transactions match' : 'No transactions yet'}</h3>
                        <p className="txns__empty-text">{hasFilters ? 'Try adjusting your filters.' : 'Transactions will appear here once payments are collected.'}</p>
                    </div>
                ) : (
                    <table className="txns__table">
                        <thead>
                            <tr>
                                <th className="txns__th txns__th--actions"></th>
                                <th className="txns__th">Date</th>
                                <th className="txns__th">Student</th>
                                <th className="txns__th">Class</th>
                                <th className="txns__th">Invoice No.</th>
                                <th className="txns__th txns__th--amount">Amount Paid</th>
                                <th className="txns__th">Method</th>
                                <th className="txns__th">Reference</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((tx, idx) => (
                                <tr
                                    className={`txns__row ${idx % 2 === 1 ? 'txns__row--even' : ''} ${drawerTx?.id === tx.id ? 'txns__row--active' : ''}`}
                                    key={tx.id}
                                    onClick={() => openDrawer(tx)}
                                >
                                    <td className="txns__td txns__td--actions">
                                        <button
                                            className="txns__print-btn"
                                            onClick={(e) => { e.stopPropagation(); handlePrintReceipt(tx); }}
                                            title="Print receipt"
                                            aria-label="Print receipt"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6 9 6 2 18 2 18 9" />
                                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                                <rect x="6" y="14" width="12" height="8" />
                                            </svg>
                                        </button>
                                    </td>
                                    <td className="txns__td txns__td--date">{formatDate(tx.transaction_date)}</td>
                                    <td className="txns__td txns__td--student">{getStudentDisplayName(tx)}</td>
                                    <td className="txns__td">{formatClassSection(tx.student?.class, tx.student?.section)}</td>
                                    <td className="txns__td txns__td--inv" title={invoiceTitle(tx)}>
                                        {invoiceLabel(tx)}
                                    </td>
                                    <td className="txns__td txns__td--amount">{formatCurrency(parseFloat(tx.amount_paid))}</td>
                                    <td className="txns__td txns__td--method">
                                        <span className={`txns__method-badge txns__method-badge--${tx.payment_method}`}>
                                            {METHOD_LABELS[tx.payment_method] ?? tx.payment_method}
                                        </span>
                                    </td>
                                    <td className="txns__td txns__td--ref">{tx.reference_number || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Pagination ── */}
            {meta && meta.last_page > 1 && (
                <div className="txns__pagination">
                    <span className="txns__pagination-info">
                        Showing {from}–{to} of {meta.total}
                    </span>
                    <div className="txns__pagination-controls">
                        <button
                            className="txns__page-btn"
                            disabled={meta.current_page <= 1}
                            onClick={() => handlePageChange(1)}
                            title="First page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></svg>
                        </button>
                        <button
                            className="txns__page-btn"
                            disabled={meta.current_page <= 1}
                            onClick={() => handlePageChange(meta.current_page - 1)}
                            title="Previous page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                        <span className="txns__page-num">
                            Page {meta.current_page} of {meta.last_page}
                        </span>
                        <button
                            className="txns__page-btn"
                            disabled={meta.current_page >= meta.last_page}
                            onClick={() => handlePageChange(meta.current_page + 1)}
                            title="Next page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                        <button
                            className="txns__page-btn"
                            disabled={meta.current_page >= meta.last_page}
                            onClick={() => handlePageChange(meta.last_page)}
                            title="Last page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></svg>
                        </button>
                    </div>
                </div>
            )}

            {/* ── Transaction Details Drawer ── */}
            {drawerTx && (() => {
                const detailState = detailByTx[drawerTx.id];
                const detail = detailState?.status === 'loaded' ? detailState.detail : undefined;
                const months = detail?.kind === 'months' ? detail : undefined;
                /* What the payment billed, regrouped across the months it was spread over —
                   the same lines the receipt prints. */
                const billed = months ? monthsReceiptParts(months.allocations, months.unallocated) : undefined;
                return (
                    <>
                        <div className="txns__drawer-backdrop" onClick={() => setDrawerTx(null)} />
                        <aside className="txns__drawer" role="dialog" aria-label={`Transaction details — ${drawerTx.invoice?.invoice_number ?? drawerTx.id}`}>
                            <header className="txns__drawer-header">
                                <div className="txns__drawer-header-left">
                                    <button className="txns__drawer-close" onClick={() => setDrawerTx(null)} aria-label="Close panel" title="Close">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                    <div>
                                        {/* A months payment belongs to a period, not to any one of the
                                            invoices it settled — so lead with the period. */}
                                        <h2 className="txns__drawer-title">
                                            {months
                                                ? formatMonthRange(months.allocations.map((a) => a.month))
                                                : drawerTx.invoice?.invoice_number ?? 'Transaction'}
                                        </h2>
                                        <p className="txns__drawer-subtitle">
                                            {getStudentDisplayName(drawerTx)}
                                            {months
                                                ? ` · ${months.allocations.length} invoice${months.allocations.length !== 1 ? 's' : ''}`
                                                : drawerTx.invoice?.title ? ` · ${drawerTx.invoice.title}` : ''}
                                        </p>
                                    </div>
                                </div>
                                <button className="txns__drawer-print" onClick={() => handlePrintReceipt(drawerTx)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6 9 6 2 18 2 18 9" />
                                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                        <rect x="6" y="14" width="12" height="8" />
                                    </svg>
                                    Print Receipt
                                </button>
                            </header>

                            <div className="txns__drawer-body">
                                {/* Payment summary */}
                                <section className="txns__drawer-section">
                                    <h4 className="txns__drawer-section-title">Payment</h4>
                                    <div className="txns__drawer-amount">
                                        <span className="txns__drawer-amount-value">{formatCurrency(parseFloat(drawerTx.amount_paid))}</span>
                                        <span className={`txns__method-badge txns__method-badge--${drawerTx.payment_method}`}>
                                            {METHOD_LABELS[drawerTx.payment_method] ?? drawerTx.payment_method}
                                        </span>
                                    </div>
                                    <div className="txns__drawer-row">
                                        <span className="txns__drawer-label">Transaction Date</span>
                                        <span className="txns__drawer-value">{formatDate(drawerTx.transaction_date)}</span>
                                    </div>
                                    <div className="txns__drawer-row">
                                        <span className="txns__drawer-label">Collected At</span>
                                        <span className="txns__drawer-value">{formatDate(drawerTx.created_at)} at {formatTime(drawerTx.created_at)}</span>
                                    </div>
                                    <div className="txns__drawer-row">
                                        <span className="txns__drawer-label">Reference No.</span>
                                        <span className="txns__drawer-value">{drawerTx.reference_number || '—'}</span>
                                    </div>
                                    {months && months.unallocated > 0 && (
                                        /* Money taken in that no invoice claimed — the student's credit,
                                           not the school's income. It belongs on the record. */
                                        <div className="txns__drawer-row">
                                            <span className="txns__drawer-label">Held as Advance</span>
                                            <span className="txns__drawer-value txns__drawer-value--advance">
                                                {formatCurrency(months.unallocated)}
                                            </span>
                                        </div>
                                    )}
                                </section>

                                {/* Student */}
                                <section className="txns__drawer-section">
                                    <h4 className="txns__drawer-section-title">Student</h4>
                                    <div className="txns__drawer-row">
                                        <span className="txns__drawer-label">Name</span>
                                        <span className="txns__drawer-value">{getStudentDisplayName(drawerTx)}</span>
                                    </div>
                                    <div className="txns__drawer-row">
                                        <span className="txns__drawer-label">Enrollment</span>
                                        <span className="txns__drawer-value">{drawerTx.student?.enrollment_number || '—'}</span>
                                    </div>
                                    <div className="txns__drawer-row">
                                        <span className="txns__drawer-label">Class</span>
                                        <span className="txns__drawer-value">{formatClassSection(drawerTx.student?.class, drawerTx.student?.section)}</span>
                                    </div>
                                </section>

                                {/* Months covered — a payment settled across several invoices. The
                                    months the money actually reached, oldest first. A month that a
                                    short payment never got to is simply not here. */}
                                {months && (
                                    <section className="txns__drawer-section">
                                        <h4 className="txns__drawer-section-title">Months Covered</h4>
                                        <table className="txns__items-table">
                                            <thead>
                                                <tr>
                                                    <th className="txns__items-th">Month</th>
                                                    <th className="txns__items-th">Invoice</th>
                                                    <th className="txns__items-th txns__items-th--amount">Allocated</th>
                                                    <th className="txns__items-th txns__items-th--amount">Balance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[...months.allocations]
                                                    .sort((a, b) => a.month.localeCompare(b.month))
                                                    .map((a) => {
                                                        const balance = parseFloat(a.invoice_balance_amount) || 0;
                                                        return (
                                                            <tr key={a.invoice_id}>
                                                                <td className="txns__items-td">{formatMonth(a.month)}</td>
                                                                <td className="txns__items-td txns__items-td--desc">{a.invoice_number}</td>
                                                                <td className="txns__items-td txns__items-td--amount">
                                                                    {formatCurrency(parseFloat(a.amount_allocated) || 0)}
                                                                </td>
                                                                <td className={`txns__items-td txns__items-td--amount${balance > 0 ? ' txns__items-td--due' : ''}`}>
                                                                    {balance > 0 ? formatCurrency(balance) : '—'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </section>
                                )}

                                {/* Line items — what was billed. For a months payment that is every
                                    invoice it touched, with a split fee put back together into the
                                    single charge the payer handed money over for. */}
                                <section className="txns__drawer-section">
                                    <h4 className="txns__drawer-section-title">{months ? 'Fees Billed' : 'Invoice Line Items'}</h4>
                                    {billed ? (
                                        <table className="txns__items-table">
                                            <thead>
                                                <tr>
                                                    <th className="txns__items-th">Description</th>
                                                    <th className="txns__items-th txns__items-th--amount">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(billed.items ?? []).map((c, i) => (
                                                    <tr key={`c${i}`}>
                                                        <td className="txns__items-td">{c.name}</td>
                                                        <td className="txns__items-td txns__items-td--amount">{formatCurrency(c.amount)}</td>
                                                    </tr>
                                                ))}
                                                {(billed.discounts ?? []).map((d, i) => (
                                                    <tr key={`d${i}`}>
                                                        <td className="txns__items-td">{d.name}</td>
                                                        <td className="txns__items-td txns__items-td--amount">−{formatCurrency(d.amount)}</td>
                                                    </tr>
                                                ))}
                                                <tr className="txns__items-total">
                                                    <td className="txns__items-td">Total Billed</td>
                                                    <td className="txns__items-td txns__items-td--amount">
                                                        {formatCurrency(billed.totalAmount ?? 0)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    ) : !drawerTx.invoice ? (
                                        <p className="txns__details-empty">Invoice details are not available for this transaction.</p>
                                    ) : !detailState || detailState.status === 'loading' ? (
                                        <div className="txns__details-loading">
                                            <div className="txns__loader">
                                                <span className="txns__loader-dot" />
                                                <span className="txns__loader-dot" />
                                                <span className="txns__loader-dot" />
                                            </div>
                                        </div>
                                    ) : detailState.status === 'error' ? (
                                        <p className="txns__details-empty">
                                            Failed to load invoice items.{' '}
                                            <button className="txns__details-retry" onClick={() => { loadDetail(drawerTx).catch(() => { /* the error state re-renders this retry */ }); }}>Retry</button>
                                        </p>
                                    ) : detail?.kind !== 'invoice' || detail.items.length === 0 ? (
                                        <p className="txns__details-empty">No line items on this invoice.</p>
                                    ) : (
                                        <table className="txns__items-table">
                                            <thead>
                                                <tr>
                                                    <th className="txns__items-th">Fee Head</th>
                                                    <th className="txns__items-th">Description</th>
                                                    <th className="txns__items-th txns__items-th--amount">Gross</th>
                                                    <th className="txns__items-th txns__items-th--amount">Discount</th>
                                                    <th className="txns__items-th txns__items-th--amount">Net</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.items.map((it) => (
                                                    <tr key={it.id}>
                                                        <td className="txns__items-td">
                                                            {it.fee_head?.name ?? '—'}
                                                            {it.is_optional && <span className="txns__items-optional">optional</span>}
                                                        </td>
                                                        <td className="txns__items-td txns__items-td--desc">{it.description || '—'}</td>
                                                        <td className="txns__items-td txns__items-td--amount">{formatCurrency(parseFloat(it.gross_amount))}</td>
                                                        <td className="txns__items-td txns__items-td--amount">
                                                            {parseFloat(it.discount_amount) > 0
                                                                ? `−${formatCurrency(parseFloat(it.discount_amount))}${it.applied_discount ? ` (${it.applied_discount.name})` : ''}`
                                                                : '—'}
                                                        </td>
                                                        <td className="txns__items-td txns__items-td--amount">{formatCurrency(parseFloat(it.net_amount))}</td>
                                                    </tr>
                                                ))}
                                                <tr className="txns__items-total">
                                                    <td className="txns__items-td" colSpan={4}>Invoice Total</td>
                                                    <td className="txns__items-td txns__items-td--amount">
                                                        {formatCurrency(detail.items.reduce((s, it) => s + parseFloat(it.net_amount), 0))}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    )}
                                </section>
                            </div>
                        </aside>
                    </>
                );
            })()}
        </div>
    );
}
